import { useContext, useEffect, useState } from "react"
import Grid from "@mui/material/Grid2";
import { pointsLayer, view, setHighlightedAccessPoints, setSelectedRiverSegment } from "../../utilities/buffaloArcGisHelper";
import Graphic from "@arcgis/core/Graphic";
import { estimateTripDurationLabel, getDistanceBetweenAccessPoints, getRiverSegment, orderAccessPointsDownstream } from "../../utilities/tripHelper";
import { Button, ClickAwayListener, IconButton, Tooltip } from "@mui/material";
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import { rootContext } from "../../contexts/rootContext";
import { PrimitiveAccessWarning } from "./PrimitiveAccessWarning";

interface TripDivProps {
    isTimeSliderOpen: Boolean
    isTripDetailsOpen: boolean
    onOpenTripDetails: (accessPoint1: string, accessPoint2: string) => void
}

export const TripDiv = (props: TripDivProps) => {   
    const context = useContext(rootContext)
    const [lastClickedAccessPoint, setLastClickedAccessPoint] = useState<string>() 
    const [selectedAccessPoint1, setSelectedAccessPoint1] = useState<string>()
    const [selectedAccessPoint2, setSelectedAccessPoint2] = useState<string>()
    const [distance, setDistance] = useState<string>()
    const [duration, setDuration] = useState<string>()
    const [isTooltipOpen, setIsTooltipOpen] = useState(false)

    useEffect(() => {
        const tryToInitHitTest = () => {
            if (view !== null) {
                view.on("click", async (event) => {
                    const hit = await view?.hitTest(event)
                    const result: any = hit?.results.find((result) =>
                        result.type === "graphic" &&
                        result.graphic.layer === pointsLayer
                    )
                    if (result == null) return
            
                    const selectedAccessPointName = (result.graphic as Graphic).attributes.Name
                    setLastClickedAccessPoint(selectedAccessPointName)
                })
            } else {
                setTimeout(tryToInitHitTest, 250)
            }
        }
        setTimeout(tryToInitHitTest, 250)
    }, [])

    useEffect(() => {
        const runAndSetDistance = async (name1: string, name2: string) => {
            const distance = await getDistanceBetweenAccessPoints(name1, name2)
            setDistance(distance)
            setDuration(estimateTripDurationLabel(parseFloat(distance)))
        }

        if (lastClickedAccessPoint !== undefined) {
            if (selectedAccessPoint1 === undefined) {
                setSelectedAccessPoint1(lastClickedAccessPoint)
                setDistance(undefined)
                setDuration(undefined)
            }
            else if (selectedAccessPoint2 === undefined) {
                // The second point picked can sit upstream of the first, so the pair is
                // reordered here to keep point 1 the put-in and point 2 the take-out.
                const [putIn, takeOut] = orderAccessPointsDownstream(selectedAccessPoint1, lastClickedAccessPoint)
                setSelectedAccessPoint1(putIn)
                setSelectedAccessPoint2(takeOut)
                runAndSetDistance(putIn, takeOut)
            }
            else {
                setSelectedAccessPoint1(lastClickedAccessPoint)
                setSelectedAccessPoint2(undefined)
                setDistance(undefined)
                setDuration(undefined)
            }
            setLastClickedAccessPoint(undefined)
        }
    }, [lastClickedAccessPoint])

    useEffect(() => {
        const names = [selectedAccessPoint1, selectedAccessPoint2].filter((name): name is string => name !== undefined)
        setHighlightedAccessPoints(names)

        if (selectedAccessPoint1 !== undefined && selectedAccessPoint2 !== undefined)
            setSelectedRiverSegment(getRiverSegment(selectedAccessPoint1, selectedAccessPoint2))
        else
            setSelectedRiverSegment(null)
    }, [selectedAccessPoint1, selectedAccessPoint2])

    // The card itself stays put so the trip is still selected on the way back,
    // but its tooltip renders above the trip details page.
    useEffect(() => {
        if (props.isTripDetailsOpen)
            setIsTooltipOpen(false)
    }, [props.isTripDetailsOpen])

    const handleClose = () => {
        setSelectedAccessPoint1(undefined)
        setSelectedAccessPoint2(undefined)
        setDistance(undefined)
        setDuration(undefined)
    }

    return ( selectedAccessPoint1 === undefined ?
    <></>
    :
    <Grid 
        container 
        sx={{ 
            position: "absolute",
            bottom: props.isTimeSliderOpen ? "130px" : "30px",
            left:"10px",
            right: { xs: "10px", sm: "auto" },
            backgroundColor: "black",
            color: "white",
            padding: "10px",
            borderRadius: "15px",
            width: { xs: "auto", sm: "400px" },
            maxWidth: "400px",
            userSelect: "none"
        }}
        spacing={1}
    >
        <Grid 
            container 
            size={12}  
            sx={{
                justifyContent: "space-between",
                alignItems: "center",
            }}
        >
            <Grid sx={{color: context.theme.text.primary}}>
                Trip
            </Grid>
            <Grid>
                <IconButton onClick={()=>handleClose()} sx={{color: context.theme.text.contrast4}}>
                    <CloseOutlinedIcon/>
                </IconButton>
            </Grid>
        </Grid>
        <Grid container spacing={2}>
            <PrimitiveAccessWarning
                accessPointNames={[selectedAccessPoint1, selectedAccessPoint2].filter((name): name is string => name !== undefined)}
                compact
            />
            <Grid size={12}>
                <span style={{color: context.theme.text.contrast5}}>Distance</span>:&nbsp;
                <span style={{color: context.theme.text.primary}}>{distance ?? "N/A"}</span>
                &nbsp;miles
            </Grid>
            <Grid size={12} sx={{display: "flex", alignItems: "center"}}>
                <span style={{color: context.theme.text.contrast5}}>Estimated Time</span>:&nbsp;
                <span style={{color: context.theme.text.primary}}>{duration ?? "N/A"}</span>
                <ClickAwayListener onClickAway={() => setIsTooltipOpen(false)}>
                    <Tooltip
                        title="Based on an average paddling speed of 2.5mph. Actual time will vary with water level, current, and stops along the way."
                        placement="top-end"
                        open={isTooltipOpen}
                        disableTouchListener
                        onOpen={() => setIsTooltipOpen(true)}
                        onClose={() => setIsTooltipOpen(false)}
                        slotProps={{ tooltip: { sx: { fontSize: "14px" } } }}
                    >
                        <IconButton
                            aria-label="Estimated trip time information"
                            onClick={() => setIsTooltipOpen(true)}
                            size="small"
                            sx={{
                                marginLeft: "2px",
                                padding: "2px",
                                color: context.theme.text.contrast2,
                                cursor: `url(${context.theme.cursor.pointer}), pointer`
                            }}
                        >
                            <InfoOutlinedIcon sx={{ fontSize: "16px" }} />
                        </IconButton>
                    </Tooltip>
                </ClickAwayListener>
            </Grid>
            <Grid size={12}>
            {selectedAccessPoint2 !== undefined ?
                <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<ArticleOutlinedIcon />}
                    onClick={() => props.onOpenTripDetails(selectedAccessPoint1, selectedAccessPoint2)}
                    sx={{
                        color: context.theme.text.contrast4,
                        borderColor: context.theme.text.contrast4,
                        cursor: `url(${context.theme.cursor.pointer}), pointer`,
                        "&:hover": {
                            borderColor: context.theme.text.contrast4,
                            backgroundColor: context.theme.misc.buttonHover
                        }
                    }}
                >
                    View & Share Trip Details
                </Button>
                :
                <span style={{color: context.theme.text.contrast2}} >Select another access point</span>
            }
            </Grid>
        </Grid>
    </Grid>)
}